import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { getLocale, formatMetric } from '@itr/shared';
import { useChartColors, useTheme } from '../theme/ThemeProvider';

/* ------------------------------------------------------------------ */
/* Sizing                                                              */
/* ------------------------------------------------------------------ */

/** Measure a container so SVG charts can be drawn in real pixels. */
export function useSize<T extends HTMLElement>(): [React.RefObject<T>, { width: number; height: number }] {
  const ref = useRef<T>(null);
  const [size, setSize] = useState({ width: 0, height: 0 });

  useLayoutEffect(() => {
    const element = ref.current;
    if (!element) return;
    const observer = new ResizeObserver(([entry]) => {
      if (!entry) return;
      const { width, height } = entry.contentRect;
      // Round to whole pixels: sub-pixel widths make crisp 1px gridlines blur.
      setSize({ width: Math.round(width), height: Math.round(height) });
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  return [ref, size];
}

/* ------------------------------------------------------------------ */
/* Tooltip                                                             */
/* ------------------------------------------------------------------ */

interface TooltipState {
  x: number;
  y: number;
  title: string;
  rows: Array<{ label: string; value: string; color: string }>;
}

function Tooltip({ state }: { state: TooltipState | null }) {
  if (!state) return null;
  // Flip to the left of the cursor near the right edge so the tooltip never
  // gets clipped by the viewport.
  const flip = state.x > window.innerWidth - 220;
  return (
    <div className="tooltip" style={{ left: flip ? state.x - 210 : state.x + 14, top: state.y - 10 }}>
      <div className="tooltip-title">{state.title}</div>
      {state.rows.map((row, i) => (
        <div className="tooltip-row" key={i}>
          <span className="tooltip-row-label">
            <span className="legend-swatch" style={{ background: row.color }} />
            {row.label}
          </span>
          <span className="tooltip-row-value">{row.value}</span>
        </div>
      ))}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Legend                                                              */
/* ------------------------------------------------------------------ */

function Legend({
  items,
  hidden,
  onToggle,
}: {
  items: Array<{ name: string; color: string }>;
  hidden: Set<string>;
  onToggle: (name: string) => void;
}) {
  // One series needs no legend — the widget title already names it.
  if (items.length < 2) return null;
  return (
    <div className="legend">
      {items.map((item) => (
        <button
          key={item.name}
          type="button"
          className="legend-item"
          data-muted={hidden.has(item.name)}
          onClick={() => onToggle(item.name)}
          style={{ background: 'none', border: 'none', padding: 0, color: 'inherit' }}
        >
          <span className="legend-swatch" style={{ background: item.color }} />
          {item.name}
        </button>
      ))}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Time series                                                         */
/* ------------------------------------------------------------------ */

export interface Series {
  name: string;
  points: Array<[number, number | null]>;
  intent?: 'neutral' | 'good' | 'warn' | 'bad';
}

/**
 * Line/area chart.
 *
 * Design rules enforced here:
 *  - one y-axis, always (a second scale makes two series silently incomparable);
 *  - nulls break the path rather than interpolating, so an outage reads as a gap
 *    instead of a straight line through missing data;
 *  - crosshair + tooltip by default;
 *  - colours are taken from the theme's fixed slot order and never cycled.
 */
export function TimeseriesChart({
  series,
  unit,
  stacked,
  height,
}: {
  series: Series[];
  unit?: string;
  stacked?: boolean;
  height?: number;
}) {
  const [ref, size] = useSize<HTMLDivElement>();
  const colors = useChartColors();
  const { theme, resolvedMode } = useTheme();
  const palette = resolvedMode === 'dark' ? theme.dark : theme.light;
  const [hidden, setHidden] = useState<Set<string>>(new Set());
  const [tooltip, setTooltip] = useState<TooltipState | null>(null);
  const [hoverIndex, setHoverIndex] = useState<number | null>(null);

  const visible = useMemo(() => series.filter((s) => !hidden.has(s.name)), [series, hidden]);

  const intentColor = useCallback(
    (intent: string | undefined, index: number) => {
      // A series that *means* good/bad wears the status colour; anything else
      // takes its categorical slot. Never both in one chart.
      if (intent === 'bad') return palette.critical;
      if (intent === 'warn') return palette.warning;
      if (intent === 'good') return palette.success;
      return colors[index % colors.length]!;
    },
    [colors, palette],
  );

  const chart = useMemo(() => {
    const padding = { top: 8, right: 10, bottom: 20, left: 44 };
    const w = Math.max(0, size.width - padding.left - padding.right);
    const h = Math.max(0, (height ?? size.height) - padding.top - padding.bottom);
    if (w <= 0 || h <= 0 || visible.length === 0) return null;

    const timestamps = visible[0]!.points.map(([ts]) => ts);
    const minTs = Math.min(...visible.flatMap((s) => s.points.map(([ts]) => ts)));
    const maxTs = Math.max(...visible.flatMap((s) => s.points.map(([ts]) => ts)));

    // Stacked series accumulate, so the domain must cover the summed height.
    const values = stacked
      ? timestamps.map((_, i) => visible.reduce((sum, s) => sum + (s.points[i]?.[1] ?? 0), 0))
      : visible.flatMap((s) => s.points.map(([, v]) => v).filter((v): v is number => v !== null));

    if (values.length === 0) return null;

    const rawMax = Math.max(...values);
    const rawMin = Math.min(0, ...values);
    // Always anchor at zero for counts and percentages: a truncated axis
    // exaggerates change, which is the classic dashboard lie.
    const maxValue = niceCeil(rawMax === rawMin ? rawMax + 1 : rawMax);
    const minValue = rawMin < 0 ? -niceCeil(-rawMin) : 0;

    const xScale = (ts: number) => (maxTs === minTs ? 0 : ((ts - minTs) / (maxTs - minTs)) * w);
    const yScale = (v: number) => h - ((v - minValue) / (maxValue - minValue)) * h;

    const stackTotals = new Array(timestamps.length).fill(0);
    const paths = visible.map((s, seriesIndex) => {
      const color = intentColor(s.intent, series.indexOf(s));
      let d = '';
      let areaD = '';
      let open = false;
      const baseline: number[] = [];

      s.points.forEach(([ts, value], i) => {
        if (value === null) {
          // Close the current run; the next point starts a new subpath.
          open = false;
          return;
        }
        const base = stacked ? stackTotals[i]! : minValue;
        const top = stacked ? base + value : value;
        baseline[i] = base;

        const x = xScale(ts);
        const y = yScale(top);
        d += `${open ? 'L' : 'M'}${x.toFixed(1)},${y.toFixed(1)}`;
        areaD += `${open ? 'L' : 'M'}${x.toFixed(1)},${y.toFixed(1)}`;
        open = true;
      });

      if (stacked) {
        // Walk back along the previous stack level to close the band.
        for (let i = s.points.length - 1; i >= 0; i--) {
          const point = s.points[i];
          if (!point || point[1] === null) continue;
          areaD += `L${xScale(point[0]).toFixed(1)},${yScale(baseline[i] ?? minValue).toFixed(1)}`;
        }
        areaD += 'Z';
        s.points.forEach(([, value], i) => {
          if (value !== null) stackTotals[i] = (stackTotals[i] ?? 0) + value;
        });
      } else if (areaD) {
        const lastPoint = [...s.points].reverse().find(([, v]) => v !== null);
        const firstPoint = s.points.find(([, v]) => v !== null);
        if (lastPoint && firstPoint) {
          areaD += `L${xScale(lastPoint[0]).toFixed(1)},${yScale(minValue).toFixed(1)}L${xScale(firstPoint[0]).toFixed(1)},${yScale(minValue).toFixed(1)}Z`;
        }
      }

      return { name: s.name, color, d, areaD, seriesIndex };
    });

    const ticks = buildTicks(minValue, maxValue, Math.max(2, Math.floor(h / 34)));
    const timeTicks = buildTimeTicks(minTs, maxTs, Math.max(2, Math.floor(w / 82)));

    return { padding, w, h, xScale, yScale, paths, ticks, timeTicks, timestamps, minTs, maxTs };
  }, [visible, series, size, height, stacked, intentColor]);

  const onMove = useCallback(
    (event: React.MouseEvent<SVGSVGElement>) => {
      if (!chart) return;
      const rect = event.currentTarget.getBoundingClientRect();
      const x = event.clientX - rect.left - chart.padding.left;
      const ratio = chart.w === 0 ? 0 : Math.max(0, Math.min(1, x / chart.w));
      const targetTs = chart.minTs + ratio * (chart.maxTs - chart.minTs);

      // Snap to the nearest sample rather than interpolating, so the tooltip
      // always shows a value that actually exists.
      let index = 0;
      let best = Infinity;
      chart.timestamps.forEach((ts, i) => {
        const distance = Math.abs(ts - targetTs);
        if (distance < best) {
          best = distance;
          index = i;
        }
      });

      setHoverIndex(index);
      setTooltip({
        x: event.clientX,
        y: event.clientY,
        title: new Date(chart.timestamps[index] ?? targetTs).toLocaleString(getLocale(), {
          month: 'short',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
        }),
        rows: visible
          .map((s, i) => ({
            label: s.name,
            value: formatMetric(s.points[index]?.[1] ?? null, unit),
            color: intentColor(s.intent, series.indexOf(s)),
          }))
          .filter((row) => row.value !== '—'),
      });
    },
    [chart, visible, series, unit, intentColor],
  );

  const toggle = (name: string) =>
    setHidden((current) => {
      const next = new Set(current);
      // Never let the user hide the last visible series — an empty chart with no
      // way back is a dead end.
      if (next.has(name)) next.delete(name);
      else if (visible.length > 1) next.add(name);
      return next;
    });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
      <div ref={ref} style={{ flex: 1, minHeight: 0, position: 'relative' }}>
        {chart && (
          <svg
            className="chart"
            width={size.width}
            height={height ?? size.height}
            onMouseMove={onMove}
            onMouseLeave={() => {
              setTooltip(null);
              setHoverIndex(null);
            }}
            role="img"
            aria-label={`Chart of ${series.map((s) => s.name).join(', ')}`}
          >
            <g transform={`translate(${chart.padding.left},${chart.padding.top})`}>
              <g className="chart-grid">
                {chart.ticks.map((tick) => (
                  <line key={tick} x1={0} x2={chart.w} y1={chart.yScale(tick)} y2={chart.yScale(tick)} />
                ))}
              </g>

              <g className="chart-axis">
                {chart.ticks.map((tick) => (
                  <text key={tick} x={-8} y={chart.yScale(tick)} dy="0.32em" textAnchor="end">
                    {compactNumber(tick, unit)}
                  </text>
                ))}
                {chart.timeTicks.map((tick) => (
                  <text key={tick.ts} x={chart.xScale(tick.ts)} y={chart.h + 14} textAnchor="middle">
                    {tick.label}
                  </text>
                ))}
              </g>

              {/* Areas first so lines always sit on top of every fill. */}
              {chart.paths.map((path) => (
                <path key={`a-${path.name}`} className="chart-area" d={path.areaD} fill={path.color} />
              ))}
              {chart.paths.map((path) => (
                <path key={`l-${path.name}`} className="chart-line" d={path.d} stroke={path.color} />
              ))}

              {hoverIndex !== null && chart.timestamps[hoverIndex] !== undefined && (
                <>
                  <line
                    className="chart-crosshair"
                    x1={chart.xScale(chart.timestamps[hoverIndex]!)}
                    x2={chart.xScale(chart.timestamps[hoverIndex]!)}
                    y1={0}
                    y2={chart.h}
                  />
                  {visible.map((s) => {
                    const value = s.points[hoverIndex]?.[1];
                    if (value === null || value === undefined) return null;
                    return (
                      <circle
                        key={`d-${s.name}`}
                        className="chart-dot"
                        cx={chart.xScale(chart.timestamps[hoverIndex]!)}
                        cy={chart.yScale(value)}
                        r={4}
                        fill={intentColor(s.intent, series.indexOf(s))}
                      />
                    );
                  })}
                </>
              )}
            </g>
          </svg>
        )}
      </div>
      <Legend
        items={series.map((s) => ({ name: s.name, color: intentColor(s.intent, series.indexOf(s)) }))}
        hidden={hidden}
        onToggle={toggle}
      />
      <Tooltip state={tooltip} />
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Sparkline                                                           */
/* ------------------------------------------------------------------ */

export function Sparkline({
  points,
  color,
  height = 30,
}: {
  points: Array<[number, number | null]>;
  color?: string;
  height?: number;
}) {
  const [ref, size] = useSize<HTMLDivElement>();
  const colors = useChartColors();
  const stroke = color ?? colors[0]!;

  const path = useMemo(() => {
    const valid = points.filter(([, v]) => v !== null) as Array<[number, number]>;
    if (valid.length < 2 || size.width === 0) return '';

    const values = valid.map(([, v]) => v);
    const min = Math.min(...values);
    const max = Math.max(...values);
    const range = max - min || 1;
    const w = size.width;
    const h = height - 4;

    return valid
      .map(([, v], i) => {
        const x = (i / (valid.length - 1)) * w;
        const y = 2 + h - ((v - min) / range) * h;
        return `${i === 0 ? 'M' : 'L'}${x.toFixed(1)},${y.toFixed(1)}`;
      })
      .join('');
  }, [points, size.width, height]);

  return (
    <div ref={ref} style={{ width: '100%', height }}>
      {path && (
        <svg width={size.width} height={height} className="chart" aria-hidden="true">
          <path d={path} fill="none" stroke={stroke} strokeWidth={1.75} strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Bars                                                                */
/* ------------------------------------------------------------------ */

export function BarList({
  bars,
  unit,
}: {
  bars: Array<{ label: string; value: number; intent?: string }>;
  unit?: string;
}) {
  const colors = useChartColors();
  const { theme, resolvedMode } = useTheme();
  const palette = resolvedMode === 'dark' ? theme.dark : theme.light;
  const max = Math.max(1, ...bars.map((b) => Math.abs(b.value)));

  const colorFor = (intent?: string) => {
    if (intent === 'bad') return palette.critical;
    if (intent === 'warn') return palette.warning;
    if (intent === 'good') return palette.success;
    // Nominal categories all take slot 1: bar length already encodes value, so
    // spending the hue channel on it would be re-encoding the same thing twice.
    return colors[0]!;
  };

  if (bars.length === 0) {
    return <div className="subtle" style={{ fontSize: 12.5, padding: '12px 0' }}>No data yet.</div>;
  }

  return (
    <div className="bars">
      {bars.map((bar, i) => (
        <div className="bar-row" key={`${bar.label}-${i}`} title={`${bar.label}: ${formatMetric(bar.value, unit)}`}>
          <span className="bar-label">{bar.label}</span>
          <span className="bar-track">
            <span
              className="bar-fill"
              style={{
                width: `${Math.max(1.5, (Math.abs(bar.value) / max) * 100)}%`,
                background: colorFor(bar.intent),
              }}
            />
          </span>
          {/* Direct value labels are the mandated relief for the light-mode
              slots that sit below 3:1 contrast — never a bare colour block. */}
          <span className="bar-value">{formatMetric(bar.value, unit)}</span>
        </div>
      ))}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Gauge                                                               */
/* ------------------------------------------------------------------ */

export function Gauge({
  value,
  min = 0,
  max = 100,
  unit,
  label,
  thresholds,
}: {
  value: number;
  min?: number;
  max?: number;
  unit?: string;
  label?: string;
  thresholds?: Array<{ at: number; intent: string }>;
}) {
  const { theme, resolvedMode } = useTheme();
  const palette = resolvedMode === 'dark' ? theme.dark : theme.light;
  const colors = useChartColors();

  const ratio = Math.max(0, Math.min(1, (value - min) / (max - min || 1)));

  // The active band is the highest threshold the value has crossed.
  const intent = useMemo(() => {
    if (!thresholds?.length) return undefined;
    const crossed = [...thresholds].sort((a, b) => a.at - b.at).filter((t) => value >= t.at);
    return crossed[crossed.length - 1]?.intent;
  }, [thresholds, value]);

  const color =
    intent === 'bad' ? palette.critical : intent === 'warn' ? palette.warning : intent === 'good' ? palette.success : colors[0]!;

  // 240° arc opening downward — the shape reads as a dial without the clutter
  // of a full ring, and leaves room for the value in the middle.
  const size = 130;
  const stroke = 11;
  const radius = (size - stroke) / 2;
  const startAngle = 150;
  const sweep = 240;
  const circumference = 2 * Math.PI * radius;
  const arcLength = (sweep / 360) * circumference;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', gap: 4 }}>
      <svg width={size} height={size * 0.78} viewBox={`0 0 ${size} ${size * 0.78}`} role="img" aria-label={label ?? 'gauge'}>
        <g transform={`translate(${size / 2}, ${size / 2}) rotate(${startAngle})`}>
          <circle
            r={radius}
            fill="none"
            stroke={palette.surfaceSunken}
            strokeWidth={stroke}
            strokeDasharray={`${arcLength} ${circumference}`}
            strokeLinecap="round"
          />
          <circle
            r={radius}
            fill="none"
            stroke={color}
            strokeWidth={stroke}
            strokeDasharray={`${arcLength * ratio} ${circumference}`}
            strokeLinecap="round"
            style={{ transition: 'stroke-dasharray 500ms cubic-bezier(0.2,0,0,1), stroke 300ms' }}
          />
        </g>
        <text
          x={size / 2}
          y={size / 2 + 2}
          textAnchor="middle"
          style={{ fontSize: 25, fontWeight: 660, letterSpacing: '-0.03em', fill: palette.text }}
        >
          {formatMetric(value, unit)}
        </text>
      </svg>
      {label && (
        <div className="subtle" style={{ fontSize: 12, textAlign: 'center', lineHeight: 1.4 }}>
          {label}
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Heatmap                                                             */
/* ------------------------------------------------------------------ */

/**
 * Sequential single-hue heatmap: light → dark maps to low → high. Never a
 * rainbow — a hue ramp has no perceptual order, so readers cannot tell which
 * cell is bigger without consulting the legend for every single one.
 */
export function Heatmap({
  xLabels,
  yLabels,
  cells,
  unit,
}: {
  xLabels: string[];
  yLabels: string[];
  cells: Array<Array<number | null>>;
  unit?: string;
}) {
  const { theme, resolvedMode } = useTheme();
  const palette = resolvedMode === 'dark' ? theme.dark : theme.light;
  const [tooltip, setTooltip] = useState<TooltipState | null>(null);

  const max = Math.max(1, ...cells.flat().map((v) => v ?? 0));
  const base = palette.chart[0]!;

  return (
    <div style={{ overflow: 'auto', height: '100%' }}>
      <div style={{ display: 'grid', gridTemplateColumns: `auto repeat(${xLabels.length}, minmax(14px, 1fr))`, gap: 2, minWidth: 480 }}>
        <div />
        {xLabels.map((label, i) => (
          <div key={label} className="subtle" style={{ fontSize: 9.5, textAlign: 'center', paddingBottom: 3 }}>
            {/* Every other tick only — 24 hour labels in a narrow widget collide. */}
            {i % 2 === 0 ? label : ''}
          </div>
        ))}

        {yLabels.map((yLabel, y) => (
          <>
            <div key={`y-${yLabel}`} className="subtle" style={{ fontSize: 10.5, paddingRight: 8, textAlign: 'right', alignSelf: 'center' }}>
              {yLabel}
            </div>
            {xLabels.map((xLabel, x) => {
              const value = cells[y]?.[x] ?? 0;
              // Non-linear ramp: activity is heavily skewed, so a linear scale
              // renders almost every cell as the palest step.
              const intensity = value === 0 ? 0 : 0.12 + 0.88 * Math.sqrt(value / max);
              return (
                <div
                  key={`${y}-${x}`}
                  style={{
                    aspectRatio: '1',
                    minHeight: 13,
                    borderRadius: 2,
                    background:
                      value === 0
                        ? palette.surfaceSunken
                        : `color-mix(in srgb, ${base} ${Math.round(intensity * 100)}%, ${palette.surface})`,
                    cursor: 'default',
                  }}
                  onMouseEnter={(e) =>
                    setTooltip({
                      x: e.clientX,
                      y: e.clientY,
                      title: `${yLabel} ${xLabel}:00`,
                      rows: [{ label: 'Calls', value: formatMetric(value, unit), color: base }],
                    })
                  }
                  onMouseLeave={() => setTooltip(null)}
                />
              );
            })}
          </>
        ))}
      </div>
      <Tooltip state={tooltip} />
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Scale helpers                                                       */
/* ------------------------------------------------------------------ */

/** Round an axis maximum up to a human number (1, 2, 2.5, 5 × 10ⁿ). */
function niceCeil(value: number): number {
  if (value <= 0) return 1;
  const exponent = Math.floor(Math.log10(value));
  const magnitude = 10 ** exponent;
  const normalised = value / magnitude;
  const nice = normalised <= 1 ? 1 : normalised <= 2 ? 2 : normalised <= 2.5 ? 2.5 : normalised <= 5 ? 5 : 10;
  return nice * magnitude;
}

function buildTicks(min: number, max: number, count: number): number[] {
  const step = niceCeil((max - min) / Math.max(1, count));
  const ticks: number[] = [];
  for (let v = Math.ceil(min / step) * step; v <= max + step * 0.001; v += step) {
    ticks.push(Number(v.toFixed(6)));
  }
  return ticks;
}

function buildTimeTicks(minTs: number, maxTs: number, count: number): Array<{ ts: number; label: string }> {
  if (!Number.isFinite(minTs) || !Number.isFinite(maxTs) || maxTs <= minTs) return [];
  const spanMs = maxTs - minTs;
  const step = spanMs / Math.max(1, count);

  // Pick the label granularity from the span: hours for a day, dates beyond.
  const format: Intl.DateTimeFormatOptions =
    spanMs < 2 * 86_400_000
      ? { hour: '2-digit', minute: '2-digit' }
      : spanMs < 60 * 86_400_000
        ? { month: 'short', day: 'numeric' }
        : { month: 'short', year: '2-digit' };

  const ticks: Array<{ ts: number; label: string }> = [];
  for (let i = 0; i <= count; i++) {
    const ts = minTs + step * i;
    ticks.push({ ts, label: new Date(ts).toLocaleString(getLocale(), format) });
  }
  return ticks;
}

/** Axis labels stay short — "1.2M" not "1,234,567". */
function compactNumber(value: number, unit?: string): string {
  if (unit === 'percent') return `${Math.round(value)}%`;
  if (unit === 'bits_per_sec' || unit === 'bytes' || unit === 'bytes_per_sec') {
    const units = unit === 'bits_per_sec' ? ['', 'K', 'M', 'G', 'T'] : ['', 'K', 'M', 'G', 'T'];
    const divisor = unit === 'bits_per_sec' ? 1000 : 1024;
    if (value === 0) return '0';
    const i = Math.min(units.length - 1, Math.floor(Math.log(Math.abs(value)) / Math.log(divisor)));
    return `${(value / divisor ** i).toFixed(i === 0 ? 0 : 1)}${units[i]}`;
  }
  if (Math.abs(value) >= 1_000_000) return `${(value / 1_000_000).toFixed(1)}M`;
  if (Math.abs(value) >= 1000) return `${(value / 1000).toFixed(1)}k`;
  return Number.isInteger(value) ? String(value) : value.toFixed(1);
}
